import React, { useState, useEffect, useRef, useContext } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar, Badge, Alert, Form } from 'react-bootstrap';
import { MicOutline, CheckmarkCircleOutline, StopOutline, LockClosedOutline } from 'react-ionicons';
import GeneralContext from '../utils/generalContext';

/**
 * Examen de Ubicación — Curso 1 "Inglés en Serio".
 * El servidor elige los ejercicios reales del curso y califica cada respuesta.
 * El navegador nunca recibe las respuestas correctas.
 */
export const PlacementTest = () => {
    const { user, global } = useContext(GeneralContext);
    const alumnoId = user && user.user && user.user.user ? user.user.user.id_alumno : null;
    const apiBase = `${global.domain}v3-api/v3/`;

    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [inactive, setInactive] = useState(false);
    const [attemptId, setAttemptId] = useState(null);
    const [step, setStep] = useState(null);
    const [result, setResult] = useState(null);

    const [textAnswer, setTextAnswer] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);

    // Pronunciación
    const [isListening, setIsListening] = useState(false);
    const [spokenText, setSpokenText] = useState('');
    const [previewScore, setPreviewScore] = useState(null);
    const [wordAccuracy, setWordAccuracy] = useState([]);
    const recognitionRef = useRef(null);
    const inputRef = useRef(null);

    const speechSupported = typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

    const preventCopyPaste = (e) => {
        e.preventDefault();
        alert('🔒 Por motivos de seguridad y validez de la evaluación, copiar o pegar texto está deshabilitado.');
    };

    const securityProps = {
        onCopy: preventCopyPaste,
        onCut: preventCopyPaste,
        onPaste: preventCopyPaste,
        onDrop: (e) => e.preventDefault(),
        onContextMenu: (e) => e.preventDefault(),
        style: { WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none', userSelect: 'none' }
    };

    const resetInputs = () => {
        setTextAnswer('');
        setSelectedOption(null);
        setSpokenText('');
        setPreviewScore(null);
        setWordAccuracy([]);
    };

    const handleServerResponse = (data) => {
        if (!data) {
            setErrorMsg('No se pudo conectar con el servidor. Intenta de nuevo.');
            return;
        }
        if (data.status === 'inactive') {
            setInactive(true);
            return;
        }
        if (data.status === 'stale' || data.status === 'no_attempt') {
            startExam();
            return;
        }
        if (data.status !== 'success') {
            setErrorMsg(data.message || 'Ocurrió un error. Intenta de nuevo.');
            return;
        }
        setAttemptId(data.attempt_id);
        if (data.finished) {
            setResult(data.result);
            setStep(null);
        } else {
            setStep(data.step);
            resetInputs();
            setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 50);
        }
    };

    const post = async (route, body) => {
        const response = await fetch(`${apiBase}${route}.json`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        return response.json();
    };

    const startExam = async () => {
        if (!alumnoId) {
            setErrorMsg('No se pudo identificar tu cuenta. Vuelve a iniciar sesión.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setErrorMsg('');
        try {
            const data = await post('placement_start', { alumnoid: alumnoId });
            handleServerResponse(data);
        } catch (e) {
            setErrorMsg('No se pudo cargar el examen. Revisa tu conexión e intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        startExam();
        return () => { if (recognitionRef.current) recognitionRef.current.abort(); };
    }, []);

    const sendAnswer = async (answer, skipped = false) => {
        if (!step || sending) return;
        setSending(true);
        setErrorMsg('');
        try {
            const data = await post('placement_answer', {
                alumnoid: alumnoId,
                attempt_id: attemptId,
                answered: step.answered,
                answer,
                skipped: skipped ? 1 : 0
            });
            handleServerResponse(data);
        } catch (e) {
            setErrorMsg('No se pudo enviar tu respuesta. Revisa tu conexión e intenta de nuevo.');
        } finally {
            setSending(false);
        }
    };

    const normalizeWords = (s) => (s || '')
        .toLowerCase()
        .replace(/[’‘´`]/g, "'")
        .replace(/[^a-z0-9' ]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    const evaluatePreview = (transcript, phrase) => {
        const target = normalizeWords(phrase);
        const pool = {};
        normalizeWords(transcript).forEach((w) => { pool[w] = (pool[w] || 0) + 1; });
        let hits = 0;
        const details = target.map((word) => {
            const matched = pool[word] > 0;
            if (matched) { hits++; pool[word]--; }
            return { word, matched };
        });
        setWordAccuracy(details);
        setPreviewScore(target.length ? Math.round((hits / target.length) * 100) : 0);
    };

    const startSpeech = () => {
        if (!speechSupported) return;
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = 'en-US';
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.onstart = () => {
            setIsListening(true);
            setSpokenText('');
            setPreviewScore(null);
            setWordAccuracy([]);
        };
        recognition.onresult = (event) => {
            let transcript = '';
            for (let i = 0; i < event.results.length; i++) {
                transcript += event.results[i][0].transcript;
            }
            setSpokenText(transcript);
            evaluatePreview(transcript, step.phrase);
        };
        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);
        recognitionRef.current = recognition;
        recognition.start();
    };

    const stopSpeech = () => {
        if (recognitionRef.current) recognitionRef.current.stop();
        setIsListening(false);
    };

    const goToCourses = () => { window.location.href = '/cursos-activos'; };

    // ---------- Pantallas ----------

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <div className="spinner-border text-primary" role="status"></div>
                <h4 className="mt-3 text-muted">Preparando tu Examen de Ubicación...</h4>
            </Container>
        );
    }

    if (inactive) {
        return (
            <Container className="py-5">
                <Alert variant="info" className="p-4 rounded-4">
                    <h4 className="fw-bold">El Examen de Ubicación no está disponible</h4>
                    <p className="mb-3">Tu profesor o la coordinación debe activarlo para tu cuenta. Si ya lo terminaste, tu resultado quedó registrado.</p>
                    <Button variant="primary" onClick={goToCourses}>Ir a mis cursos</Button>
                </Alert>
            </Container>
        );
    }

    if (result) {
        const stats = [
            { label: 'Ejercicios contestados', value: result.answered },
            { label: 'Respuestas correctas', value: result.correct },
            { label: 'Escritura / Gramática', value: result.written_pct !== null ? `${result.written_pct}%` : '—' },
            { label: 'Pronunciación', value: result.pronunciation_avg !== null ? `${result.pronunciation_avg}%` : '—' }
        ];
        return (
            <Container className="py-5" {...securityProps}>
                <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
                    <div className="bg-primary bg-gradient text-white p-4 text-center">
                        <CheckmarkCircleOutline color="#ffffff" height="64px" width="64px" />
                        <h2 className="fw-bold mt-2">¡Examen de Ubicación terminado!</h2>
                        <p className="lead mb-0">Tu resultado quedó registrado para tu profesor.</p>
                    </div>
                    <Card.Body className="p-4 p-md-5">
                        <Alert variant="success" className="rounded-3 p-4">
                            <h5 className="fw-bold mb-2">🎯 Tu punto de inicio en Inglés en Serio:</h5>
                            <p className="display-6 fw-semibold text-dark mb-1">{result.placement_titulo}</p>
                            {result.placement_level && <Badge bg="dark" className="fs-6">{result.placement_level}</Badge>}
                            <hr />
                            <p className="mb-0">{result.message}</p>
                        </Alert>
                        <Row className="g-3 my-3">
                            {stats.map((s) => (
                                <Col md={3} sm={6} key={s.label}>
                                    <Card className="text-center h-100 border-light bg-light shadow-sm">
                                        <Card.Body>
                                            <h6 className="text-muted fw-bold">{s.label}</h6>
                                            <h3 className="fw-bold text-primary mb-0">{s.value}</h3>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                        <div className="d-flex justify-content-end mt-4">
                            <Button variant="success" size="lg" onClick={goToCourses}>Ir a mis cursos</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (!step) {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="p-4 rounded-4">
                    <p className="mb-3">{errorMsg || 'No se pudo cargar el examen.'}</p>
                    <Button variant="primary" onClick={startExam}>Reintentar</Button>
                </Alert>
            </Container>
        );
    }

    const hasReading = step.context && step.context.length > 280;
    const progressPct = Math.round(((step.block - 1) / step.blocks) * 100);
    const canSubmit = step.kind === 'mc' ? selectedOption !== null
        : step.kind === 'pron' ? spokenText.trim() !== ''
        : textAnswer.trim() !== '';

    const submit = () => {
        if (!canSubmit) return;
        if (step.kind === 'mc') sendAnswer(String(selectedOption));
        else if (step.kind === 'pron') { stopSpeech(); sendAnswer(spokenText); }
        else sendAnswer(textAnswer);
    };

    return (
        <Container fluid="lg" className="py-4" {...securityProps}>
            <div className="d-flex justify-content-between align-items-center bg-dark text-white p-2 px-3 rounded-top-3 small">
                <span>
                    <LockClosedOutline color="#00e676" height="16px" width="16px" /> <strong>Evaluación segura:</strong> copiar y pegar desactivados.
                </span>
                <span>Respuestas enviadas: {step.answered}</span>
            </div>

            <Card className="mb-4 shadow-sm border-0 rounded-bottom-4 rounded-top-0">
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col md={8}>
                            <div className="mb-2">
                                <Badge bg="primary" className="me-2 px-3 py-2 fw-normal">Examen de Ubicación • Inglés en Serio</Badge>
                                {step.level && <Badge bg="dark" className="me-2 px-3 py-2 fw-normal">{step.level}</Badge>}
                                <Badge bg={step.unit_kind === 'review' ? 'warning' : 'info'} text="dark" className="px-3 py-2 fw-normal">
                                    {step.unit_kind === 'review' ? 'Review' : 'Módulo'}
                                </Badge>
                            </div>
                            <h3 className="fw-bold mb-1">{step.unit_title}</h3>
                            <p className="text-muted mb-0">Ejercicio {step.step} de {step.unit_steps}</p>
                        </Col>
                        <Col md={4} className="text-md-end mt-3 mt-md-0">
                            <h6 className="text-muted">Avance en el curso</h6>
                            <ProgressBar now={Math.max(progressPct, 3)} label={`${progressPct}%`} variant="info" style={{ height: '14px', borderRadius: '10px' }} />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Row className="g-4">
                {hasReading && (
                    <Col lg={5}>
                        <Card className="shadow-sm border-0 rounded-4 sticky-top" style={{ top: '20px' }}>
                            <Card.Header className="bg-dark text-white fw-bold py-3 rounded-top-4">📖 Lectura</Card.Header>
                            <Card.Body className="p-4 overflow-auto" style={{ maxHeight: '550px', fontSize: '1.05rem', lineHeight: '1.7' }}>
                                <p className="text-secondary mb-0" style={{ whiteSpace: 'pre-line' }}>{step.context}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                <Col lg={hasReading ? 7 : 12}>
                    <Card className="shadow-sm border-0 rounded-4">
                        <Card.Header className="bg-light py-3">
                            <span className="fw-bold">{step.instruction || 'Ejercicio'}</span>
                            {!hasReading && step.context && <div className="small text-muted mt-1">{step.context}</div>}
                        </Card.Header>
                        <Card.Body className="p-4">
                            {step.kind !== 'pron' && <h4 className="fw-semibold mb-4">{step.question}</h4>}

                            {step.kind === 'text' && (
                                <Form onSubmit={(e) => { e.preventDefault(); submit(); }}>
                                    <Form.Control
                                        ref={inputRef}
                                        size="lg"
                                        type="text"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        placeholder="Escribe tu respuesta en inglés"
                                        value={textAnswer}
                                        onChange={(e) => setTextAnswer(e.target.value)}
                                        onPaste={preventCopyPaste}
                                        onDrop={(e) => e.preventDefault()}
                                        style={{ userSelect: 'text' }}
                                    />
                                </Form>
                            )}

                            {step.kind === 'mc' && (
                                <div className="d-flex flex-column gap-2">
                                    {step.options.map((opt, idx) => (
                                        <Button
                                            key={opt.id}
                                            variant={selectedOption === opt.id ? 'primary' : 'outline-secondary'}
                                            className="text-start py-3 px-4 rounded-3 fw-medium"
                                            onClick={() => setSelectedOption(opt.id)}
                                        >
                                            {String.fromCharCode(65 + idx)}. {opt.text}
                                        </Button>
                                    ))}
                                </div>
                            )}

                            {step.kind === 'pron' && (
                                <div>
                                    <h5 className="fw-semibold mb-2">{step.question}</h5>
                                    <Alert variant="info" className="rounded-3 p-3 my-3">
                                        <h4 className="fw-bold text-dark mb-0">"{step.phrase}"</h4>
                                    </Alert>
                                    {speechSupported ? (
                                        <div className="text-center py-3">
                                            {!isListening ? (
                                                <Button variant="danger" size="lg" className="rounded-circle p-4 shadow mb-2" onClick={startSpeech}>
                                                    <MicOutline color="#ffffff" height="36px" width="36px" />
                                                </Button>
                                            ) : (
                                                <Button variant="warning" size="lg" className="rounded-circle p-4 shadow mb-2" onClick={stopSpeech}>
                                                    <StopOutline color="#000000" height="36px" width="36px" />
                                                </Button>
                                            )}
                                            <div>
                                                <small className="text-muted fw-bold">
                                                    {isListening ? 'Escuchando... lee la oración ahora.' : 'Presiona el micrófono y lee la oración en voz alta. Puedes intentarlo de nuevo antes de enviar.'}
                                                </small>
                                            </div>
                                        </div>
                                    ) : (
                                        <Alert variant="warning">Tu navegador no permite evaluar la voz. Usa Google Chrome o Edge, o continúa sin esta parte.</Alert>
                                    )}
                                    {spokenText && (
                                        <div className="bg-light p-3 rounded-3 mt-2 border text-center">
                                            <h6 className="text-muted fw-bold mb-2">Lo que se escuchó:</h6>
                                            <p className="fs-5 mb-2">
                                                {wordAccuracy.map((w, i) => (
                                                    <span key={i} className={`badge me-1 fs-6 ${w.matched ? 'bg-success' : 'bg-danger'}`}>{w.word}</span>
                                                ))}
                                            </p>
                                            {previewScore !== null && (
                                                <Badge bg={previewScore >= 80 ? 'success' : 'warning'} text={previewScore >= 80 ? undefined : 'dark'} className="p-2 fs-6">
                                                    Coincidencia: {previewScore}% {previewScore >= 80 ? '✓' : '(se necesita 80%)'}
                                                </Badge>
                                            )}
                                        </div>
                                    )}
                                    <div className="text-center mt-3">
                                        <Button variant="link" className="text-muted small" disabled={sending}
                                            onClick={() => { if (window.confirm('¿Continuar sin evaluar tu pronunciación? Tu profesor verá que se omitió.')) { stopSpeech(); sendAnswer('', true); } }}>
                                            No puedo usar el micrófono
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {errorMsg && <Alert variant="danger" className="mt-3 mb-0">{errorMsg}</Alert>}

                            <div className="d-flex justify-content-end mt-4">
                                <Button variant="primary" size="lg" className="px-5 py-3 rounded-3 fw-bold shadow" disabled={!canSubmit || sending} onClick={submit}>
                                    {sending ? 'Enviando...' : 'Responder y continuar →'}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                    <p className="text-muted small mt-3 mb-0">
                        El examen avanza por niveles. Si tienes 2 errores seguidos en un módulo, el examen termina y ahí empezarás tu curso.
                    </p>
                </Col>
            </Row>
        </Container>
    );
};

export default PlacementTest;
